import type { BannerAdProvider } from "@yjsgames/ad-ports";
import { currentCrazyGamesSdk, initCrazyGamesSdk } from "./CrazyGamesSdk.js";
import { PALETTE } from "../themes/palette.js";
import {
  getCrazyGamesBannerSlot,
  subscribeCrazyGamesBannerSlotChange,
  type CrazyGamesBannerSlot,
} from "./CrazyGamesBannerSlot.js";
import {
  isCrazyGamesBannerBlockedError,
  setCrazyGamesBannerReadiness,
} from "./CrazyGamesBannerReadiness.js";

const CG_DIAG = (import.meta.env["VITE_WEB_AD_PROVIDER"] as string | undefined) === "crazygames";

function diag(message: string, data?: Record<string, unknown>): void {
  if (!CG_DIAG) return;
  console.info(`[CG-DIAG][BannerProvider] ${message} ${JSON.stringify(data ?? {})}`);
}

export interface CrazyGamesBannerProviderOptions {
  readonly devFallback?: boolean;
}

interface BannerSize {
  readonly width: number;
  readonly height: number;
}

const STATIC_BANNER_SIZES: readonly BannerSize[] = [
  { width: 728, height: 90 },
  { width: 468, height: 60 },
  { width: 320, height: 100 },
  { width: 320, height: 50 },
  { width: 300, height: 250 },
];
const BANNER_WIDTH_TOLERANCE = 24;
const BANNER_HEIGHT_FALLBACK_MIN = 32;

/**
 * CrazyGames 웹 배너 — `CrazyGames.SDK.banner.requestBanner` (DIP BannerAdProvider 구현).
 *
 * DOM 컨테이너를 canvas 위에 overlay로 생성하고, show 시 SDK에 배너 요청,
 * remove 시 컨테이너를 숨긴다. 멱등 — 중복 show/remove 안전.
 *
 * CrazyGames 배너는 surface 판정(`resolveBannerSurface`)에 따라 BannerController가
 * show/remove를 호출한다.
 *
 * containerId는 CrazyGames SDK가 배너를 렌더링할 DOM 요소의 id.
 */
export class CrazyGamesBannerProvider implements BannerAdProvider {
  private container: HTMLDivElement | null = null;
  private currentSize: BannerSize | null = null;
  private requestedSize: BannerSize | null = null;
  private shown = false;
  private unsubscribeSlotChange: (() => void) | null = null;
  private pendingResizeRequest = false;
  private readonly containerId: string;
  private readonly devFallback: boolean;
  private readonly handleSlotChange = (): void => {
    if (!this.shown || !this.container) return;
    const placement = this.syncContainerToSlot(this.container);
    if (!placement.size) {
      this.container.style.display = "none";
      this.requestedSize = null;
      return;
    }
    if (!this.shouldRequestSizeChange(placement.size)) return;
    if (!currentCrazyGamesSdk() && this.devFallback) {
      this.requestedSize = placement.size;
      return;
    }
    void this.refreshBannerForSizeChange();
  };

  constructor(
    containerId = "crazygames-banner",
    _width = 728,
    _height = 90,
    options: CrazyGamesBannerProviderOptions = {},
  ) {
    this.containerId = containerId;
    this.devFallback = options.devFallback ?? false;
  }

  async showBanner(): Promise<boolean> {
    if (this.shown) {
      diag("showBanner skipped already shown", { containerId: this.containerId });
      return true;
    }
    const el = this.ensureContainer();
    const placement = this.syncContainerToSlot(el);
    diag("showBanner start", {
      containerId: this.containerId,
      placement,
      hasSdk: Boolean(currentCrazyGamesSdk()),
      devFallback: this.devFallback,
    });
    if (!placement.size) {
      el.style.display = "none";
      console.warn("[CrazyGames] banner slot has no supported size", placement.slot);
      return false;
    }
    this.shown = true;
    this.unsubscribeSlotChange = subscribeCrazyGamesBannerSlotChange(this.handleSlotChange);
    if (!currentCrazyGamesSdk() && this.devFallback) {
      this.renderDevFallback(el);
      this.requestedSize = placement.size;
      setCrazyGamesBannerReadiness("ready");
      diag("showBanner dev fallback", { containerId: this.containerId });
      return true;
    }
    const requested = await this.requestBanner();
    diag("showBanner requested", { requested, size: this.currentSize });
    if (!requested) {
      this.shown = false;
      this.unsubscribeSlotChange?.();
      this.unsubscribeSlotChange = null;
      el.style.display = "none";
    }
    return requested;
  }

  removeBanner(): void {
    if (!this.shown) {
      diag("removeBanner skipped not shown", { containerId: this.containerId });
      return;
    }
    this.shown = false;
    this.unsubscribeSlotChange?.();
    this.unsubscribeSlotChange = null;
    this.requestedSize = null;
    const sdk = currentCrazyGamesSdk();
    diag("removeBanner", { containerId: this.containerId, hasSdk: Boolean(sdk) });
    void sdk?.banner.clearBanner?.(this.containerId);
    if (this.container) {
      this.container.style.display = "none";
    }
  }

  private async requestBanner(): Promise<boolean> {
    const sdk = currentCrazyGamesSdk() ?? (await initCrazyGamesSdk(true));
    if (!sdk || !this.shown) {
      diag("requestBanner skipped", { hasSdk: Boolean(sdk), shown: this.shown });
      return false;
    }
    const size = this.currentSize;
    if (!size) {
      diag("requestBanner no size", {});
      return false;
    }
    try {
      diag("requestBanner call", { containerId: this.containerId, size, sdkEnvironment: sdk.environment });
      await sdk.banner.requestBanner({
        id: this.containerId,
        width: size.width,
        height: size.height,
      });
      this.requestedSize = { ...size };
      setCrazyGamesBannerReadiness("ready");
      diag("requestBanner success", { shown: this.shown });
      return this.shown;
    } catch (error) {
      if (isCrazyGamesBannerBlockedError(error)) {
        setCrazyGamesBannerReadiness("blocked");
      }
      diag("requestBanner failed", { error, sdkEnvironment: sdk.environment, size });
      console.warn("[CrazyGames] banner request failed", error);
      return false;
    }
  }

  private ensureContainer(): HTMLDivElement {
    if (this.container) return this.container;
    let el = document.getElementById(this.containerId) as HTMLDivElement | null;
    if (!el) {
      el = document.createElement("div");
      el.id = this.containerId;
    }
    el.style.cssText = this.containerCss();
    if (el.parentElement !== document.body) {
      document.body.appendChild(el);
    }
    this.container = el;
    return el;
  }

  private containerCss(): string {
    return [
      "position:fixed",
      "z-index:9999",
      "display:none",
      "box-sizing:border-box",
      "overflow:hidden",
      "pointer-events:auto",
    ].join(";");
  }

  private syncContainerToSlot(
    el: HTMLDivElement,
  ): { readonly slot: CrazyGamesBannerSlot | null; readonly size: BannerSize | null } {
    const slot = getCrazyGamesBannerSlot();
    if (!slot) {
      this.currentSize = null;
      diag("syncContainerToSlot none", {});
      return { slot, size: null };
    }
    const viewportWidth = typeof window === "undefined" ? slot.width : window.innerWidth;
    const size = selectStaticBannerSize(slot, viewportWidth);
    if (!size) {
      this.currentSize = null;
      diag("syncContainerToSlot unsupported", { slot, viewportWidth });
      return { slot, size: null };
    }
    this.currentSize = size;
    el.style.left = `${bannerLeft(slot, size)}px`;
    el.style.top = `${Math.round(slot.top + (slot.height - size.height) / 2)}px`;
    el.style.width = `${size.width}px`;
    el.style.height = `${size.height}px`;
    el.style.display = "block";
    return { slot, size };
  }

  private renderDevFallback(el: HTMLDivElement): void {
    el.textContent = "DEV RESPONSIVE BANNER";
    el.style.display = "flex";
    el.style.alignItems = "center";
    el.style.justifyContent = "center";
    el.style.font = "700 12px system-ui, sans-serif";
    el.style.letterSpacing = "0";
    el.style.color = PALETTE.appBackground;
    el.style.background =
      `repeating-linear-gradient(135deg, ${PALETTE.accentGold} 0 14px, ${PALETTE.accentAmber} 14px 28px)`;
    el.style.border = "1px solid rgba(0,0,0,0.25)";
    el.style.boxShadow = "0 4px 14px rgba(0,0,0,0.18)";
  }

  private shouldRequestSizeChange(size: BannerSize): boolean {
    return !this.requestedSize ||
      this.requestedSize.width !== size.width ||
      this.requestedSize.height !== size.height;
  }

  private async refreshBannerForSizeChange(): Promise<void> {
    if (this.pendingResizeRequest) return;
    this.pendingResizeRequest = true;
    const sdk = currentCrazyGamesSdk();
    try {
      diag("refreshBannerForSizeChange", {
        containerId: this.containerId,
        requestedSize: this.requestedSize,
        currentSize: this.currentSize,
        hasSdk: Boolean(sdk),
      });
      await sdk?.banner.clearBanner?.(this.containerId);
      const requested = await this.requestBanner();
      if (!requested && this.shown) {
        this.shown = false;
        this.unsubscribeSlotChange?.();
        this.unsubscribeSlotChange = null;
        this.container?.style.setProperty("display", "none");
      }
    } finally {
      this.pendingResizeRequest = false;
    }
  }
}

export function selectStaticBannerSize(
  slot: Pick<CrazyGamesBannerSlot, "width" | "height">,
  viewportWidth = slot.width,
): BannerSize | null {
  const inlineSize = STATIC_BANNER_SIZES.find((size) => (
    slot.width + BANNER_WIDTH_TOLERANCE >= size.width && slot.height >= size.height
  ));
  if (inlineSize) return inlineSize;

  if (slot.height >= BANNER_HEIGHT_FALLBACK_MIN && viewportWidth >= 320) {
    return { width: 320, height: 50 };
  }
  return null;
}

function bannerLeft(
  slot: Pick<CrazyGamesBannerSlot, "left" | "width">,
  size: BannerSize,
): number {
  const raw = slot.left + (slot.width - size.width) / 2;
  const max = typeof window === "undefined" ? raw : window.innerWidth - size.width;
  return Math.round(Math.max(0, Math.min(raw, max)));
}
